import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/api';
import { SiteAnalysisResult } from '../types';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import { useAuthStore } from '../store/authStore';
import RegionSelector from './RegionSelector';
import { normalizeImageUrl } from '../utils/imageUtils';

// Новая цветовая схема
const FigmaColors = {
  background: '#FFFFFF',
  darkBg: '#d0e1f9',
  inputBg: 'rgb(248, 242, 255)', // Фон полей форм
  inputPlaceholder: '#000000',
  primaryButton: '#4d648d',
  primaryButtonGradient: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
  chipBorder: '#4d648d',
  progressBg: '#d0e1f9',
  progressFill: '#4d648d',
  spacerDark: '#d0e1f9',
  headingColor: '#000000', // Цвет заголовков
};

interface OnboardingFlowProps {
  onComplete: () => void;
}

interface OnboardingData {
  url?: string;
  analysis?: SiteAnalysisResult;
  campaign_id?: number;
  creatives?: {
    keywords?: string[];
    negatives?: string[];
    ads?: any[];
    graphic?: any;
    images?: Array<{
      id: number;
      url: string;
      size: string;
      concept_index: number;
      concept_name: string;
      prompt: string;
    }>;
  };
  site_analysis?: {
    summary?: string;
    brand?: string;
    site_text?: string;
  };
}

interface CampaignFormData {
  goal: 'sales' | 'leads' | 'awareness' | null;
  budget: string;
  geo: string | null; // Now supports any region slug
}


interface OrganizationData {
  organization_name: string;
  organization_type: 'individual' | 'legal' | 'entrepreneur' | null;
  tin: string;
  representative_last_name: string;
  representative_first_name: string;
  representative_middle_name: string;
  organization_email: string;
  registration_country: string;
  registration_region: string;
}

// Общие пропсы для шагов. Шаги игнорируют ненужные пропсы.
interface StepProps {
  nextStep: () => void;
  prevStep?: () => void;
  animatedProgress: number;
  onComplete: () => void;
  onboardingData: OnboardingData;
  setOnboardingData: (data: OnboardingData) => void;
  user?: any; // Telegram user data
  isAnalyzing?: boolean;
  analysisError?: string;
  campaignData?: CampaignFormData;
  setCampaignData?: React.Dispatch<React.SetStateAction<CampaignFormData>>;
  organizationData?: OrganizationData;
  setOrganizationData?: React.Dispatch<React.SetStateAction<OrganizationData>>;
  isSubmitting?: boolean;
  submitError?: string;
  handleCampaignSubmit?: (campaignPayload: any) => Promise<boolean>;
  handleOrganizationSubmit?: (organizationData: OrganizationData) => Promise<boolean>;
}

function Step1({ nextStep }: StepProps) {
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 'calc(100vh - 120px)', // Account for header space
      textAlign: 'center', 
      px: 2 
    }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
          Быстрый запуск рекламы
        </Typography>
        <Typography variant="body2" sx={{ 
          color: '#000000', 
          mb: 3, 
          mt: 4,
          fontSize: '16px',
          bgcolor: 'rgb(248, 242, 255)',
          borderRadius: '10px',
          p: 2
        }}>
          Запустите рекламу в несколько кликов, всю работу за вас сделает ИИ
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          {[
            { icon: '🚫', text: 'Без логина Yandex/VK' },
            { icon: '🏢', text: 'Всё на агентских аккаунтах' },
            { icon: '🤖', text: 'Рекламу готовит ИИ' },
            { icon: '📊', text: 'Статистика и оплата в одном месте' },
          ].map((item, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              p: 2
            }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: 20
              }}>
                {item.icon}
              </Box>
              <Typography variant="body2" sx={{ color: '#000000', fontSize: '16px' }}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Button
        variant="contained"
        fullWidth
        onClick={nextStep}
        sx={{
          background: FigmaColors.primaryButtonGradient,
          color: '#ffffff !important',
          borderRadius: 3,
          height: 48,
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': { 
            background: FigmaColors.primaryButtonGradient,
            opacity: 0.9,
            boxShadow: 'none',
            color: '#ffffff !important'
          },
        }}
      >
        Начать настройку
      </Button>
    </Box>
  );
}

function Step2({ nextStep, onboardingData, setOnboardingData }: StepProps) {
  const [url, setUrl] = useState(onboardingData.url || '');
  const [error, setError] = useState<string>('');

  const handleSubmit = () => {
    setError('');
    
    if (!url.trim()) {
      setError('Введите ссылку на сайт');
      return;
    }

    // Простая валидация URL на клиенте
    const trimmedUrl = url.trim();
    const hasProtocol = /^https?:\/\//i.test(trimmedUrl);
    const candidate = hasProtocol ? trimmedUrl : `https://${trimmedUrl}`;

    try {
      new URL(candidate); // Базовая проверка формата URL
    } catch {
      setError('Введите корректную ссылку на сайт');
      return;
    }

    // Сохраняем URL и переходим к Step 3 для анализа
    setOnboardingData({
      ...onboardingData,
      url: candidate,
      analysis: undefined // Очищаем предыдущий анализ
    });
    
    nextStep();
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 'calc(100vh - 120px)', // Account for header space
      px: 2 
    }}>
      <Box sx={{ 
        flex: 1,
        mt: '30vh' // 30% of viewport height top margin
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
          Давайте познакомимся, введите ссылку на ваш сайт:
        </Typography>
        
        <Box
          sx={{
            mb: 3,
            bgcolor: FigmaColors.inputBg,
            borderRadius: '36px',
            p: 2,
          }}
        >
          <input
            type="text"
            placeholder="example.com или https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: FigmaColors.inputPlaceholder,
              fontSize: '16px',
              fontFamily: 'inherit',
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        disabled={!url.trim()}
        sx={{
          background: FigmaColors.primaryButtonGradient,
          color: '#ffffff !important',
          boxShadow: 'none',
          borderRadius: 3,
          height: 48,
          fontWeight: 700,
          mb: 2, // Add bottom margin to match Step 1
          '&:hover': { 
            background: FigmaColors.primaryButtonGradient,
            opacity: 0.9,
            boxShadow: 'none',
            color: '#ffffff !important'
          },
        }}
      >
        Анализировать веб-сайт
      </Button>
    </Box>
  );
}

function Step3({ nextStep, onboardingData, setOnboardingData, user }: StepProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string>('');
  const [analysisStarted, setAnalysisStarted] = useState(false);

  // Если анализ уже есть (данные загружены), сразу переходим к следующему шагу
  useEffect(() => {
    if (onboardingData.analysis) {
      console.log('✅ Step3: Анализ уже есть, переходим к следующему шагу');
      setTimeout(() => {
        nextStep();
      }, 500);
    }
  }, [onboardingData.analysis]);

  // Если URL пустой - это обрабатывается в главном компоненте (возврат на шаг 2)

  // Запускаем анализ при загрузке компонента
  useEffect(() => {
    const startAnalysis = async () => {
      // Проверяем, что анализ еще не начинался и есть URL, и анализа еще нет
      if (analysisStarted || !onboardingData.url || onboardingData.analysis || isAnalyzing) {
        return;
      }

      setAnalysisStarted(true);
      setIsAnalyzing(true);
      setAnalysisError('');

      try {
        const result = await apiService.analyzeOnboardingSite(onboardingData.url, user?.id);
        
        // Сохраняем результат анализа
        const updatedData: OnboardingData = {
          ...onboardingData,
          url: result.url,
          analysis: result.analysis
        };
        setOnboardingData(updatedData);

        // Небольшая задержка для показа завершения, затем переход
        setTimeout(() => {
          nextStep();
        }, 800);

      } catch (error: any) {
        console.error('Site analysis error:', error);
        setAnalysisError(error?.message || 'Не удалось проанализировать сайт. Попробуйте еще раз.');
        setIsAnalyzing(false);
        setAnalysisStarted(false); // Позволяем повторить анализ
      }
    };

    startAnalysis();
  }, [onboardingData.url, onboardingData.analysis, analysisStarted, isAnalyzing]);

  // Автоматический переход к Step 4 когда анализ завершен - убран, чтобы избежать двойного перехода

  const getProgressValue = () => {
    if (onboardingData.analysis) return 100;
    if (analysisError) return 0;
    return undefined; // Indeterminate
  };

  const getStatusText = () => {
    if (onboardingData.analysis) return 'Завершено!';
    if (analysisError) return 'Ошибка анализа';
    return 'Анализируем...';
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 'calc(100vh - 120px)', 
      px: 2,
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{ textAlign: 'center', maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: FigmaColors.headingColor }}>
          Анализируем сайт...
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ 
              color: analysisError ? '#f44336' : '#1e1f26', 
              fontWeight: 600 
            }}>
              {getStatusText()}
            </Typography>
          </Box>
          <Box sx={{ 
            borderRadius: 0.5, 
            p: 1.5
          }}>
            <LinearProgress
              variant={getProgressValue() !== undefined ? "determinate" : "indeterminate"}
              value={getProgressValue()}
              sx={{
                height: 8,
                borderRadius: 0.5,
                bgcolor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  background: analysisError ? '#f44336' : FigmaColors.primaryButtonGradient,
                  borderRadius: 0.5,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {analysisError && (
        <Box sx={{ mt: 3, maxWidth: 400, width: '100%' }}>
          <Alert severity="error" sx={{ mb: 2, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
            {analysisError}
          </Alert>
          {/* Показываем кнопку "Попробовать ещё раз" только если URL есть */}
          {onboardingData.url && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setAnalysisError('');
                setIsAnalyzing(false);
                setAnalysisStarted(false);
                // Перезапуск анализа произойдет автоматически через useEffect
              }}
              sx={{
                borderColor: '#e0e0e0',
                color: '#000000',
                bgcolor: '#ffffff',
                borderRadius: 3,
                height: 48,
                fontWeight: 700,
                '&:hover': { 
                  borderColor: '#e0e0e0',
                  bgcolor: '#f5f5f5',
                  color: '#000000',
                },
              }}
            >
              Попробовать еще раз
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

// Остальные шаги ниже определены как внешние компоненты

function Step4({ 
  nextStep, 
  onboardingData, 
  campaignData, 
  setCampaignData, 
  isSubmitting, 
  submitError,
  handleCampaignSubmit
}: StepProps) {
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { resetOnboarding } = useAuthStore();
  const [isResetting, setIsResetting] = useState(false);

  // Если кампания уже создана и есть все данные, можно пропустить создание
  const campaignAlreadyCreated = !!onboardingData.campaign_id;

  const handleInputChange = (field: keyof CampaignFormData, value: string | null) => {
    if (setCampaignData) {
      setCampaignData(prev => ({ ...prev, [field]: value }));
    }
    // Очищаем ошибку для этого поля
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!campaignData?.goal) {
      errors.goal = 'Выберите цель кампании';
    }
    
    if (!campaignData?.budget?.trim()) {
      errors.budget = 'Бюджет обязателен';
    } else {
      const budgetNum = parseFloat(campaignData.budget);
      console.log('💰 Budget validation:', { 
        original: campaignData.budget, 
        parsed: budgetNum, 
        isNaN: isNaN(budgetNum), 
        isPositive: budgetNum > 0 
      });
      if (isNaN(budgetNum) || budgetNum <= 0) {
        errors.budget = 'Введите корректный бюджет';
      }
    }
    
    if (!campaignData?.geo) {
      errors.geo = 'Выберите географию';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !onboardingData.analysis || !onboardingData.url) {
      return;
    }

    // Если кампания уже создана, просто переходим дальше
    if (campaignAlreadyCreated) {
      console.log('✅ Step4: Кампания уже создана (ID:', onboardingData.campaign_id, '), переходим дальше');
      nextStep();
      return;
    }

    if (!handleCampaignSubmit) {
      return;
    }

    // Генерируем название кампании автоматически
    const siteName = new URL(onboardingData.url).hostname.replace('www.', '');
    const goalText = campaignData!.goal === 'sales' ? 'Продажи' :
                    campaignData!.goal === 'leads' ? 'Лиды' :
                    campaignData!.goal === 'awareness' ? 'Узнаваемость' : 'Продажи';
    const generatedName = `Кампания ${siteName} ${goalText}`;

    const campaignPayload = {
      name: generatedName,
      goal: campaignData!.goal!,
      budget: parseFloat(campaignData!.budget),
      geo: campaignData!.geo!,
      business_profile: onboardingData.analysis,
      site_url: onboardingData.url,
    };

    const success = await handleCampaignSubmit(campaignPayload);
    if (success) {
      nextStep(); // Переходим к следующему шагу после успешного сохранения
    }
  };

  const goalMapping = {
    'Продажи': 'sales' as const,
    'Лиды': 'leads' as const,
    'Узнаваемость': 'awareness' as const,
  };

  const handleEnterAnotherSite = async () => {
    setIsResetting(true);
    try {
      await resetOnboarding();
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to reset onboarding:', error);
      setIsResetting(false);
    }
  };


  return (
    <Box sx={{ px: 2 }}>
      <Card elevation={0} sx={{ bgcolor: FigmaColors.inputBg, borderRadius: '36px', mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: FigmaColors.headingColor, mb: 2 }}>
            Портрет бизнеса
          </Typography>
          
          {onboardingData.analysis ? (
            <Box>
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                <strong>Описание:</strong> {onboardingData.analysis.summary}
              </Typography>
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                <strong>Источник анализа:</strong> {onboardingData.analysis.source === 'ai' ? 'ИИ-анализ' : 'Эвристический анализ'}
              </Typography>
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                <strong>Язык:</strong> {onboardingData.analysis.lang}
                </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
              Анализ сайта не завершен
            </Typography>
          )}
        </CardContent>
      </Card>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
        Выберите параметры кампании
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}


      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: FigmaColors.headingColor }}>
          Цель кампании
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(goalMapping).map(([label, value]) => (
            <Chip
              key={label}
              label={label}
              clickable
              variant={campaignData?.goal === value ? "filled" : "outlined"}
              onClick={() => handleInputChange('goal', value)}
              sx={{
                borderColor: campaignData?.goal === value ? '#000000' : '#ececfc',
                border: campaignData?.goal === value ? '1px solid #000000' : '1px solid #ececfc',
                color: campaignData?.goal === value ? '#ffffff' : '#514b81',
                bgcolor: campaignData?.goal === value ? '#000000' : '#ffffff',
                '&:hover': {
                  bgcolor: campaignData?.goal === value ? '#000000' : '#ffffff',
                  opacity: 0.8,
                },
              }}
            />
          ))}
        </Box>
        {formErrors.goal && (
          <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
            {formErrors.goal}
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: FigmaColors.headingColor }}>
          Бюджет (в рублях)
        </Typography>
        <Box
          sx={{
            bgcolor: FigmaColors.inputBg,
            borderRadius: '36px',
            p: 2,
            border: formErrors.budget ? '1px solid #f44336' : 'none',
          }}
        >
          <input
            type="number"
            placeholder="Введите бюджет рекламной кампании в рублях"
            value={campaignData?.budget || ''}
            onChange={(e) => handleInputChange('budget', e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: FigmaColors.inputPlaceholder,
              fontSize: '16px',
              fontFamily: 'inherit',
            }}
          />
        </Box>
        {formErrors.budget && (
          <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
            {formErrors.budget}
          </Typography>
        )}
      </Box>

      <RegionSelector
        selectedRegion={campaignData?.geo || null}
        onRegionChange={(regionSlug) => handleInputChange('geo', regionSlug)}
        error={formErrors.geo}
      />

      {campaignAlreadyCreated && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Кампания уже создана. Вы можете изменить параметры и продолжить.
        </Alert>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        disabled={isSubmitting || !onboardingData.analysis}
        sx={{
          background: FigmaColors.primaryButtonGradient,
          color: '#ffffff !important',
          boxShadow: 'none',
          borderRadius: 3,
          height: 48,
          fontWeight: 700,
          '&:hover': { 
            background: FigmaColors.primaryButtonGradient,
            opacity: 0.9,
            boxShadow: 'none',
            color: '#ffffff !important'
          },
          '&:disabled': { 
            bgcolor: FigmaColors.inputBg, 
            color: '#000000' 
          },
        }}
      >
        {isSubmitting ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} sx={{ color: FigmaColors.darkBg }} />
            Сохраняем...
          </Box>
        ) : campaignAlreadyCreated ? (
          'Продолжить'
        ) : (
          'Создать кампанию'
        )}
      </Button>

      <Button
        variant="outlined"
        fullWidth
        onClick={handleEnterAnotherSite}
        disabled={isSubmitting || isResetting}
        sx={{
          mt: 2,
          borderColor: '#283655',
          color: '#000000',
          borderRadius: 3,
          height: 48,
          fontWeight: 600,
          '&:hover': {
            borderColor: '#283655',
            backgroundColor: 'rgba(40, 54, 85, 0.04)'
          },
          '&:disabled': { 
            borderColor: FigmaColors.inputBg, 
            color: '#999999' 
          },
        }}
      >
        {isResetting ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} sx={{ color: '#283655' }} />
            Сбрасываем...
          </Box>
        ) : (
          'Ввести другой сайт'
        )}
      </Button>
    </Box>
  );
}


function Step6({ nextStep, onboardingData, setOnboardingData, campaignData }: StepProps) {
  const { user } = useTelegramWebApp();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState('Подготавливаем генерацию...');
  const [generationStarted, setGenerationStarted] = useState(false);
  const generationInProgress = useRef(false); // Дополнительная защита от повторных вызовов

  // Если креативы уже есть (данные загружены), сразу переходим к следующему шагу
  useEffect(() => {
    if (onboardingData.creatives && 
        (onboardingData.creatives.keywords?.length || 
         onboardingData.creatives.ads?.length ||
         onboardingData.creatives.negatives?.length)) {
      console.log('✅ Step6: Креативы уже есть, переходим к следующему шагу');
      setProgress(100);
      setCurrentTask('Рекламная кампания готова!');
      setTimeout(() => {
        nextStep();
      }, 500);
    }
  }, [onboardingData.creatives]);

  // Запускаем генерацию сразу при загрузке компонента
  useEffect(() => {
    const startGeneration = async () => {
      // Проверяем, что генерация еще не начиналась и у нас есть все необходимые данные
      const hasExistingCreatives = onboardingData.creatives && 
        (onboardingData.creatives.keywords?.length || 
         onboardingData.creatives.ads?.length ||
         onboardingData.creatives.negatives?.length);

      if (generationStarted || generationInProgress.current || !onboardingData.url || !onboardingData.analysis || hasExistingCreatives || isGenerating) {
        console.log('🔄 Step6: Skipping generation - waiting for conditions', {
          generationStarted,
          generationInProgress: generationInProgress.current,
          hasUrl: !!onboardingData.url,
          hasAnalysis: !!onboardingData.analysis,
          hasCreatives: !!hasExistingCreatives,
          isGenerating,
          hasUser: !!user
        });
        return;
      }

      // Если пользователь еще не загружен, ждем его
      if (!user) {
        console.log('🔄 Step6: Waiting for user to load...');
        return;
      }

      console.log('🚀 Step6: Starting creative generation immediately');
      generationInProgress.current = true;
      setGenerationStarted(true);
      setIsGenerating(true);
      setGenerationError('');
      setProgress(10); // Начинаем с 10%
      setCurrentTask('Создаем рекламную кампанию...');

      try {
        // Получаем данные для генерации
        const goal = campaignData?.goal === 'sales' ? 'Увеличение продаж' :
                    campaignData?.goal === 'leads' ? 'Генерация лидов' :
                    campaignData?.goal === 'awareness' ? 'Повышение узнаваемости' : 'Увеличение продаж';
        
        const geo = campaignData?.geo === 'moscow' ? 'Москва' :
                   campaignData?.geo === 'spb' ? 'Санкт-Петербург' :
                   campaignData?.geo === 'russia' ? 'Россия' : 'Россия';

        // Обновляем прогресс перед вызовом API
        setProgress(20);
        setCurrentTask('Анализируем контент сайта...');

        console.log('📡 Step6: Calling API generateOnboardingTextCreatives');
        
        // Создаем интервал для обновления прогресса во время API вызова
        const progressInterval = setInterval(() => {
          setProgress(prev => {
            if (prev < 85) {
              const increment = Math.random() * 5 + 2; // Случайное увеличение от 2 до 7%
              return Math.min(prev + increment, 85);
            }
            return prev;
          });
        }, 1000);

        // Обновляем задачи периодически
        const taskInterval = setInterval(() => {
          const tasks = [
            'Анализируем контент сайта...',
            'Генерируем ключевые слова...',
            'Создаем минус-слова...',
            'Готовим рекламные объявления...',
            'Финализируем объявления...'
          ];
          setCurrentTask(tasks[Math.floor(Math.random() * tasks.length)]);
        }, 3000);

        const result: {
          campaign_id: number;
          creatives: {
            keywords?: string[];
            negatives?: string[];
            ads?: any[];
          };
          errors: Record<string, string>;
          site_analysis?: {
            summary?: string;
            brand?: string;
            site_text?: string;
          };
        } = await apiService.generateOnboardingTextCreatives({
          telegram_user_id: user.id,
          url: onboardingData.url!,
          goal,
          audience: 'Целевая аудитория',
          geo,
          campaign_id: onboardingData.campaign_id
        });

        // Очищаем интервалы
        clearInterval(progressInterval);
        clearInterval(taskInterval);

        console.log('✅ Step6: API call successful', result);
        console.log('✅ Step6: Saving campaign_id:', result.campaign_id);
        
        // Завершаем прогресс
        setProgress(100);
        setCurrentTask('Рекламная кампания готова!');
        
        // Сохраняем результат генерации в новом формате
        const updatedData: OnboardingData = {
          ...onboardingData,
          campaign_id: result.campaign_id,
          creatives: result.creatives,
          site_analysis: result.site_analysis
        };
        console.log('✅ Step6: Updated onboarding data:', updatedData);
        setOnboardingData(updatedData);
        setIsGenerating(false);

        // Небольшая задержка для показа завершения, затем переход
        setTimeout(() => {
          console.log('➡️ Step6: Moving to next step');
          generationInProgress.current = false; // Сбрасываем флаг
          nextStep();
        }, 1000);

      } catch (error: any) {
        console.error('❌ Step6: Creative generation error:', error);
        setGenerationError(error?.message || 'Не удалось создать рекламную кампанию. Попробуйте еще раз.');
        setIsGenerating(false);
        setGenerationStarted(false); // Позволяем повторить генерацию
        generationInProgress.current = false; // Сбрасываем флаг при ошибке
        setProgress(0);
        setCurrentTask('');
      }
    };

    startGeneration();
  }, [onboardingData.url, onboardingData.analysis, user]); // Запускаем когда есть все необходимые данные

  const getProgressValue = () => {
    if (onboardingData.creatives) return 100;
    if (generationError) return 0;
    return progress;
  };

  const getStatusText = () => {
    if (onboardingData.creatives) return 'Рекламная кампания готова!';
    if (generationError) return 'Ошибка генерации';
    return currentTask || 'Создаем рекламную кампанию...';
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 'calc(100vh - 120px)', 
      px: 2,
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{ textAlign: 'center', maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: FigmaColors.headingColor }}>
          Создаем рекламную кампанию
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ 
              color: generationError ? '#f44336' : '#1e1f26', 
              fontWeight: 600 
            }}>
              {getStatusText()}
            </Typography>
          </Box>
          <Box sx={{ 
            borderRadius: 0.5, 
            p: 1.5
          }}>
            <LinearProgress
              variant={getProgressValue() !== undefined ? "determinate" : "indeterminate"}
              value={getProgressValue()}
              sx={{
                height: 8,
                borderRadius: 0.5,
                bgcolor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  background: generationError ? '#f44336' : FigmaColors.primaryButtonGradient,
                  borderRadius: 0.5,
                },
              }}
            />
          </Box>
        </Box>
      </Box>



      {generationError && (
        <Box sx={{ mb: 4, maxWidth: 400, width: '100%' }}>
          <Alert severity="error" sx={{ mb: 2, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
            {generationError}
          </Alert>
        <Button
            variant="outlined"
            onClick={async () => {
              console.log('🔄 Step6: Retry button clicked');
              setGenerationError('');
              setIsGenerating(false);
              setGenerationStarted(false);
              setProgress(10);
              setCurrentTask('Повторная попытка...');
              generationInProgress.current = false; // Сбрасываем флаг для повторной попытки
              
              // Запускаем генерацию заново сразу
              if (!generationInProgress.current && !onboardingData.creatives && user) {
                console.log('🚀 Step6: Restarting generation after retry');
                generationInProgress.current = true;
                setGenerationStarted(true);
                setIsGenerating(true);
                setProgress(20);
                setCurrentTask('Повторно анализируем контент...');

                try {
                  // Получаем данные для генерации
                  const goal = campaignData?.goal === 'sales' ? 'Увеличение продаж' :
                              campaignData?.goal === 'leads' ? 'Генерация лидов' :
                              campaignData?.goal === 'awareness' ? 'Повышение узнаваемости' : 'Увеличение продаж';
                  
                  const geo = campaignData?.geo === 'moscow' ? 'Москва' :
                             campaignData?.geo === 'spb' ? 'Санкт-Петербург' :
                             campaignData?.geo === 'russia' ? 'Россия' : 'Россия';

                  // Создаем интервал для обновления прогресса во время API вызова
                  const progressInterval = setInterval(() => {
                    setProgress(prev => {
                      if (prev < 85) {
                        const increment = Math.random() * 5 + 2;
                        return Math.min(prev + increment, 85);
                      }
                      return prev;
                    });
                  }, 1000);

                  // Обновляем задачи периодически
                  const taskInterval = setInterval(() => {
                    const tasks = [
                      'Повторно анализируем контент...',
                      'Генерируем ключевые слова...',
                      'Создаем минус-слова...',
                      'Готовим рекламные объявления...',
                      'Финализируем объявления...'
                    ];
                    setCurrentTask(tasks[Math.floor(Math.random() * tasks.length)]);
                  }, 3000);

                  console.log('📡 Step6: Calling API generateOnboardingTextCreatives (retry)');
                  const result: {
                    campaign_id: number;
                    creatives: {
                      keywords?: string[];
                      negatives?: string[];
                      ads?: any[];
                    };
                    errors: Record<string, string>;
                    site_analysis?: {
                      summary?: string;
                      brand?: string;
                      site_text?: string;
                    };
                  } = await apiService.generateOnboardingTextCreatives({
                    telegram_user_id: user.id,
                    url: onboardingData.url!,
                    goal,
                    audience: 'Целевая аудитория',
                    geo
                  });

                  // Очищаем интервалы
                  clearInterval(progressInterval);
                  clearInterval(taskInterval);

                  console.log('✅ Step6: API call successful (retry)', result);
                  setProgress(100);
                  setCurrentTask('Рекламная кампания готова!');
                  
                  // Сохраняем результат генерации в новом формате
                  const updatedData: OnboardingData = {
                    ...onboardingData,
                    campaign_id: result.campaign_id,
                    creatives: result.creatives,
                    site_analysis: result.site_analysis
                  };
                  setOnboardingData(updatedData);
                  setIsGenerating(false);

                  // Небольшая задержка для показа завершения, затем переход
                  setTimeout(() => {
                    console.log('➡️ Step6: Moving to next step (retry)');
                    generationInProgress.current = false;
                    nextStep();
                  }, 1000);

                } catch (error: any) {
                  console.error('❌ Step6: Creative generation error (retry):', error);
                  setGenerationError(error?.message || 'Не удалось создать рекламную кампанию. Попробуйте еще раз.');
                  setIsGenerating(false);
                  setGenerationStarted(false);
                  generationInProgress.current = false;
                  setProgress(0);
                  setCurrentTask('');
                }
              }
            }}
          sx={{
              borderColor: '#e0e0e0',
              color: '#000000',
              bgcolor: '#ffffff',
              '&:hover': {
                borderColor: '#e0e0e0',
                bgcolor: '#f5f5f5',
                color: '#000000',
              },
            }}
          >
            Попробовать снова
        </Button>
      </Box>
      )}
    </Box>
  );
}

function Step7({ nextStep, onboardingData, setOnboardingData, campaignData }: StepProps) {
  // Проверяем, есть ли уже изображения при инициализации
  const hasExistingImages = !!(onboardingData.creatives?.images && onboardingData.creatives.images.length > 0);
  
  const [showBanners, setShowBanners] = useState(false);
  const [isLoadingGraphics, setIsLoadingGraphics] = useState(false);
  const [graphicsError, setGraphicsError] = useState<string>('');
  const [graphicsLoaded, setGraphicsLoaded] = useState(hasExistingImages); // Если изображения уже есть, считаем их загруженными
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  const [generatedImagesCount, setGeneratedImagesCount] = useState(hasExistingImages ? onboardingData.creatives!.images!.length : 0);
  const [activeNegatives, setActiveNegatives] = useState<string[]>([]);
  const [isDeletingNegative, setIsDeletingNegative] = useState<string | null>(null);
  const [activeKeywords, setActiveKeywords] = useState<string[]>([]);
  const [isDeletingKeyword, setIsDeletingKeyword] = useState<string | null>(null);
  const [isRegeneratingTextCreatives, setIsRegeneratingTextCreatives] = useState(false);
  const [textCreativesError, setTextCreativesError] = useState<string>('');
  const { user } = useTelegramWebApp();

  console.log('🎯 Step7: Component mounted with data:', {
    campaignId: onboardingData.campaign_id,
    hasCreatives: !!onboardingData.creatives,
    userId: user?.id
  });

  // Новая структура данных - объявления приходят напрямую из API
  const creatives = onboardingData.creatives || {};
  
  // Извлекаем данные из новой структуры
  const keywords = creatives.keywords || [];
  const negatives = creatives.negatives || [];
  const ads = creatives.ads || [];
  const graphicCreative = creatives.graphic;
  const images = creatives.images || [];

  // Инициализируем активные ключевые слова и минус-слова при первой загрузке
  useEffect(() => {
    if (keywords.length > 0 && activeKeywords.length === 0) {
      setActiveKeywords([...keywords]);
    }
    if (negatives.length > 0 && activeNegatives.length === 0) {
      setActiveNegatives([...negatives]);
    }
  }, [keywords, negatives, activeKeywords.length, activeNegatives.length]);

  // Функция для удаления ключевого слова
  const handleDeleteKeyword = async (keyword: string) => {
    if (isDeletingKeyword) {
      return;
    }

    setIsDeletingKeyword(keyword);

    try {
      // Сначала удаляем локально для мгновенной обратной связи
      setActiveKeywords(prev => prev.filter(k => k !== keyword));

      // Затем пытаемся удалить на сервере
      if (user && onboardingData.campaign_id) {
        console.log('🔍 Attempting to delete keyword:', {
          keyword,
          user_id: user.id,
          campaign_id: onboardingData.campaign_id
        });
        
        await apiService.deleteKeyword({
          telegram_user_id: user.id,
          campaign_id: onboardingData.campaign_id,
          keyword: keyword
        });
        console.log('✅ Keyword deleted successfully:', keyword);
      } else {
        console.warn('⚠️ Missing user or campaign_id:', {
          user: !!user,
          campaign_id: onboardingData.campaign_id
        });
      }
    } catch (error: any) {
      console.error('❌ Failed to delete keyword:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // В случае ошибки API, восстанавливаем ключевое слово в UI
      setActiveKeywords(prev => {
        if (!prev.includes(keyword)) {
          return [...prev, keyword];
        }
        return prev;
      });
      
      // Показываем пользователю ошибку
      alert(`Не удалось удалить ключевое слово "${keyword}": ${error.message}`);
    } finally {
      setIsDeletingKeyword(null);
    }
  };

  // Функция для удаления минус-слова
  const handleDeleteNegative = async (negative: string) => {
    if (isDeletingNegative) {
      return;
    }

    setIsDeletingNegative(negative);

    try {
      // Сначала удаляем локально для мгновенной обратной связи
      setActiveNegatives(prev => prev.filter(n => n !== negative));

      // Затем пытаемся удалить на сервере
      if (user && onboardingData.campaign_id) {
        await apiService.deleteNegativeKeyword({
          telegram_user_id: user.id,
          campaign_id: onboardingData.campaign_id,
          negative: negative
        });
        console.log('✅ Negative keyword deleted successfully:', negative);
      }
    } catch (error: any) {
      console.error('❌ Failed to delete negative keyword:', error);
      // В случае ошибки API, минус-слово уже удалено локально, оставляем как есть
    } finally {
      setIsDeletingNegative(null);
    }
  };

  // Функция для загрузки баннеров
  const loadGraphicCreatives = async () => {
    console.log('🎨 Step7: loadGraphicCreatives called', {
      hasUser: !!user,
      userId: user?.id,
      campaignId: onboardingData.campaign_id,
      graphicsLoaded,
      isLoadingGraphics
    });

    if (!user || !onboardingData.campaign_id || graphicsLoaded || isLoadingGraphics) {
      console.log('🎨 Step7: Skipping graphic creatives loading', {
        reason: !user ? 'no user' : !onboardingData.campaign_id ? 'no campaign_id' : graphicsLoaded ? 'already loaded' : 'already loading'
      });
      return;
    }

    console.log('🎨 Step7: Starting graphic creatives loading');
    setIsLoadingGraphics(true);
    setGraphicsError('');

    try {
      const result = await apiService.generateOnboardingGraphicCreatives({
        telegram_user_id: user.id,
        campaign_id: onboardingData.campaign_id
      });

      console.log('✅ Step7: Graphic creatives loaded successfully', result);

      // Обновляем данные онбординга с баннерами
      const updatedData: OnboardingData = {
        ...onboardingData,
        creatives: {
          ...onboardingData.creatives,
          graphic: result.creatives.graphic,
          images: result.creatives.images || []
        }
      };
      setOnboardingData(updatedData);
      setGraphicsLoaded(true);

    } catch (error: any) {
      console.error('❌ Step7: Failed to load graphic creatives:', error);
      setGraphicsError(error?.message || 'Не удалось загрузить баннеры');
    } finally {
      setIsLoadingGraphics(false);
    }
  };

  // Функция для принудительной перезагрузки баннеров
  const retryLoadGraphicCreatives = async () => {
    console.log('🔄 Step7: Retrying graphic creatives loading');
    
    // Сбрасываем состояния для принудительной перезагрузки
    setGraphicsLoaded(false);
    setGraphicsError('');
    setIsLoadingGraphics(false);
    setGeneratedImagesCount(0);
    
    // Очищаем существующие баннеры
    const updatedData: OnboardingData = {
      ...onboardingData,
      creatives: {
        ...onboardingData.creatives,
        graphic: undefined,
        images: []
      }
    };
    setOnboardingData(updatedData);
    
    // Запускаем загрузку заново
    await loadGraphicCreatives();
  };

  // Функция для повторной генерации текстовых объявлений
  const regenerateTextCreatives = async () => {
    if (!user || !onboardingData.campaign_id || !onboardingData.url || isRegeneratingTextCreatives) {
      return;
    }

    console.log('🔄 Step7: Starting text creatives regeneration');
    setIsRegeneratingTextCreatives(true);
    setTextCreativesError('');

    try {
      // Получаем данные кампании для повторной генерации
      const goal = campaignData?.goal === 'sales' ? 'Увеличение продаж' :
                  campaignData?.goal === 'leads' ? 'Генерация лидов' :
                  campaignData?.goal === 'awareness' ? 'Повышение узнаваемости' : 'Увеличение продаж';
      
      const geo = campaignData?.geo === 'moscow' ? 'Москва' :
                 campaignData?.geo === 'spb' ? 'Санкт-Петербург' :
                 campaignData?.geo === 'russia' ? 'Россия' : 'Россия';

      const result = await apiService.generateOnboardingTextCreatives({
        telegram_user_id: user.id,
        url: onboardingData.url,
        goal,
        audience: 'Целевая аудитория',
        geo,
        campaign_id: onboardingData.campaign_id
      });

      console.log('✅ Step7: Text creatives regenerated successfully', result);

      // Обновляем данные онбординга с новыми текстовыми объявлениями
      const updatedData: OnboardingData = {
        ...onboardingData,
        creatives: {
          ...onboardingData.creatives,
          keywords: result.creatives.keywords,
          negatives: result.creatives.negatives,
          ads: result.creatives.ads
        }
      };
      setOnboardingData(updatedData);

      // Обновляем активные ключевые слова и минус-слова
      setActiveKeywords(result.creatives.keywords || []);
      setActiveNegatives(result.creatives.negatives || []);

    } catch (error: any) {
      console.error('❌ Step7: Failed to regenerate text creatives:', error);
      setTextCreativesError(error?.message || 'Не удалось создать объявления повторно');
    } finally {
      setIsRegeneratingTextCreatives(false);
    }
  };

  // Автоматически загружаем баннеры при монтировании компонента
  // Но только если их еще нет
  useEffect(() => {
    const existingImages = onboardingData.creatives?.images || [];
    if (user && onboardingData.campaign_id && !graphicsLoaded && !isLoadingGraphics && existingImages.length === 0) {
      loadGraphicCreatives();
    }
  }, [user, onboardingData.campaign_id]);

  // Инициализируем счетчик изображений
  useEffect(() => {
    if (images.length > 0 && generatedImagesCount === 0) {
      setGeneratedImagesCount(images.length);
    }
  }, [images.length, generatedImagesCount]);

  // Загружаем баннеры при переключении на баннеры
  const handleShowBanners = () => {
    setShowBanners(true);
    if (!graphicsLoaded && !isLoadingGraphics) {
      loadGraphicCreatives();
    }
  };

  // Генерируем дополнительное изображение
  const handleGenerateMore = async () => {
    if (generatedImagesCount >= 3 || isGeneratingMore || !user) {
      return;
    }

    setIsGeneratingMore(true);
    
    try {
      // Получаем visual_plan из первого концепта для генерации
      const firstConcept = onboardingData.creatives?.graphic?.concepts?.[0];
      if (!firstConcept?.visual_plan) {
        throw new Error('Не найден план для генерации изображения');
      }

      console.log('🎨 Generating additional image with visual_plan:', firstConcept.visual_plan);

      // Вызываем API для генерации изображения
      const imageResult = await apiService.generateImage(
        firstConcept.visual_plan,
        '1024x1024',
        'url'
      );

      if (imageResult?.url) {
        // Создаем новое изображение для добавления в список
        const newImage = {
          id: Date.now(), // Временный ID
          url: imageResult.url,
          size: '1024x1024',
          concept_index: 0,
          concept_name: firstConcept.name || 'Дополнительный концепт',
          prompt: firstConcept.visual_plan
        };

        // Обновляем данные онбординга с новым изображением
        const updatedData = {
          ...onboardingData,
          creatives: {
            ...onboardingData.creatives,
            images: [...(onboardingData.creatives?.images || []), newImage]
          }
        };
        
        setOnboardingData(updatedData);
        setGeneratedImagesCount(prev => prev + 1);
        
        console.log('✅ Additional image generated successfully');
      }
    } catch (error: any) {
      console.error('❌ Failed to generate additional image:', error);
      setGraphicsError(error?.message || 'Не удалось сгенерировать дополнительное изображение');
    } finally {
      setIsGeneratingMore(false);
    }
  };

  return (
    <Box sx={{ px: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
        Создаём объявления в Яндекс.Директ
      </Typography>

      {/* Контент */}
      {!showBanners ? (
        <Box sx={{ mb: 4 }}>
          {/* Ключевые слова */}
          {activeKeywords.length > 0 && (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
          Ключевые слова
        </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {activeKeywords.slice(0, 10).map((keyword: string, index: number) => (
          <Chip 
                    key={index}
                    label={keyword.trim()}
                    onDelete={isDeletingKeyword === keyword ? undefined : () => handleDeleteKeyword(keyword)}
                    deleteIcon={
                      isDeletingKeyword === keyword ? (
                        <CircularProgress size={16} sx={{ color: '#ececfc' }} />
                      ) : undefined
                    }
                    sx={{ 
                      bgcolor: '#ffffff', 
                      color: '#514b81', 
                      border: '1px solid #ececfc',
                      '& .MuiChip-deleteIcon': {
                        color: '#666666',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          color: '#333333',
                          backgroundColor: 'transparent'
                        }
                      }
                    }} 
                  />
                ))}
        </Box>
            </Box>
          )}
        
          {/* Минус-слова */}
          {activeNegatives.length > 0 && (
            <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
          "Минус" слова
        </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {activeNegatives.slice(0, 10).map((negative: string, index: number) => (
          <Chip 
                    key={index}
                    label={`-${negative.trim()}`}
                    onDelete={isDeletingNegative === negative ? undefined : () => handleDeleteNegative(negative)}
                    deleteIcon={
                      isDeletingNegative === negative ? (
                        <CircularProgress size={16} sx={{ color: '#ececfc' }} />
                      ) : undefined
                    }
                    sx={{ 
                      bgcolor: '#ffffff', 
                      color: '#514b81', 
                      border: '1px solid #ececfc',
                      '& .MuiChip-deleteIcon': {
                        color: '#666666',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          color: '#333333',
                          backgroundColor: 'transparent'
                        }
                      }
                    }}
                  />
                ))}
        </Box>
            </Box>
          )}

          {/* Рекламные объявления */}
          {ads.length > 0 && (
            <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
          Тексты объявлений
        </Typography>
        
              {ads.slice(0, 3).map((ad: any, index: number) => (
          <Card key={index} sx={{ bgcolor: 'rgb(248, 242, 255)', border: 'none', boxShadow: 'none', mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
               {(ad.header || ad.title || ad.Title) && (
                 <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: FigmaColors.headingColor }}>
                      {ad.header || ad.title || ad.Title}
                    </Typography>
               )}
                    <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                      {ad.text || ad.Text || ad.description || 'Описание объявления'}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
          )}

          {/* Если нет данных */}
          {keywords.length === 0 && negatives.length === 0 && ads.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                Рекламная кампания не была создана
              </Typography>
              
              {textCreativesError && (
                <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                  {textCreativesError}
                </Alert>
              )}
              
              <Button
                variant="contained"
                onClick={regenerateTextCreatives}
                disabled={isRegeneratingTextCreatives}
                sx={{
                  bgcolor: FigmaColors.primaryButton,
                  color: FigmaColors.darkBg,
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  '&:hover': { 
                    bgcolor: FigmaColors.primaryButton,
                    opacity: 0.9
                  },
                  '&:disabled': {
                    bgcolor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                {isRegeneratingTextCreatives ? 'Создаем...' : 'Создать объявления'}
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ mb: 4 }}>
          {/* Прогресс-бар при загрузке баннеров */}
          {isLoadingGraphics && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, mt: '50px', color: FigmaColors.headingColor, textAlign: 'center' }}>
                Генерируем баннеры...
              </Typography>
              <LinearProgress 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  bgcolor: 'transparent',
                  '& .MuiLinearProgress-bar': {
                    background: FigmaColors.primaryButtonGradient,
                    borderRadius: 4,
                  }
                }} 
              />
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mt: 1, textAlign: 'center' }}>
                Генерация занимает не более 1 минуты
              </Typography>
            </Box>
          )}

          {/* Ошибка загрузки баннеров */}
          {graphicsError && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#d32f2f', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#000000' }}>
                {graphicsError}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={retryLoadGraphicCreatives}
                disabled={isLoadingGraphics}
                sx={{
                  mt: 1,
                  bgcolor: 'white',
                  color: '#d32f2f',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  '&:disabled': {
                    bgcolor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                {isLoadingGraphics ? 'Загружаем...' : 'Попробовать снова'}
              </Button>
            </Box>
          )}

          {/* Сгенерированные изображения */}
          {images.length > 0 && (
            <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
                Сгенерированные баннеры
      </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {images.map((image, index) => (
                  <Card elevation={0} key={image.id} sx={{ 
                    bgcolor: FigmaColors.inputBg, 
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}>
        <Box sx={{ 
                      width: '100%', 
                      minHeight: 300, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: '#f5f5f5'
                    }}>
                      {image.url ? (
                        <img 
                          src={normalizeImageUrl(image.url)} 
                          alt={`Баннер ${index + 1}`}
                          style={{ 
                            width: '100%', 
                            height: 'auto', 
                            objectFit: 'contain' 
                          }}
                          onError={(e) => {
                            console.error('Image load error:', e);
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
                          Изображение недоступно
                        </Typography>
                      )}
        </Box>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#000000',
                        fontSize: '0.8rem',
                        mb: 1
                      }}>
                        {image.size} • {image.concept_name}
              </Typography>
                    </CardContent>
        </Card>
      ))}
              </Box>
              
              {/* Кнопка "Сгенерировать ещё" */}
              {generatedImagesCount < 3 && (
                <Button
                  variant="contained"
                  onClick={handleGenerateMore}
                  disabled={isGeneratingMore}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: '#000000',
                    color: '#ffffff',
                    fontWeight: 700,
                    '&:hover': { 
                      bgcolor: '#1e2a3f'
                    },
                    '&:disabled': {
                      bgcolor: '#cccccc',
                      color: '#666666'
                    }
                  }}
                >
                  {isGeneratingMore ? 'Генерируем...' : `Сгенерировать ещё (${generatedImagesCount}/3)`}
                </Button>
              )}
            </Box>
          )}

          {/* Графический концепт */}
          {(graphicCreative || images.length > 0) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
                Концепт баннера
              </Typography>
              <Card elevation={0} sx={{ 
                bgcolor: FigmaColors.inputBg, 
                borderRadius: 2,
                mb: 2,
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
                    {images.length > 0 && images[0].prompt ? images[0].prompt : 
                     (graphicCreative?.content || 'Описание графического концепта')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Если нет данных и не загружаем */}
          {images.length === 0 && !graphicCreative && !isLoadingGraphics && !graphicsError && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                Баннеры еще не загружены
              </Typography>
              <Button
                variant="contained"
                onClick={retryLoadGraphicCreatives}
                disabled={isLoadingGraphics}
                sx={{
                  bgcolor: FigmaColors.primaryButton,
                  color: FigmaColors.darkBg,
                  '&:hover': { bgcolor: FigmaColors.primaryButton },
                  '&:disabled': {
                    bgcolor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                {isLoadingGraphics ? 'Загружаем...' : 'Загрузить баннеры'}
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={showBanners ? nextStep : handleShowBanners}
        sx={{
          background: FigmaColors.primaryButtonGradient,
          color: '#ffffff !important',
          boxShadow: 'none',
          borderRadius: 3,
          height: 48,
          fontWeight: 700,
          '&:hover': { 
            background: FigmaColors.primaryButtonGradient,
            opacity: 0.9,
            boxShadow: 'none',
            color: '#ffffff !important'
          },
        }}
      >
        {showBanners ? 'Сохранить и перейти дальше' : 'Посмотреть баннеры'}
      </Button>
    </Box>
  );
}

function Step8({ nextStep, user, onboardingData }: StepProps) {
  const [activeTab, setActiveTab] = useState(1); // Начинаем с вкладки "Баннеры"
  const [banners, setBanners] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Загружаем баннеры при загрузке компонента
  useEffect(() => {
    const loadBanners = async () => {
      if (!user?.id) {
        console.log('🔍 Step8: No user ID available');
        return;
      }
      
      console.log('🔍 Step8: Loading banners', {
        userId: user.id,
        campaignId: onboardingData?.campaign_id,
        hasOnboardingData: !!onboardingData,
        onboardingDataKeys: onboardingData ? Object.keys(onboardingData) : []
      });
      
      setLoading(true);
      setError('');
      
      try {
        // Используем campaign_id если есть, иначе загружаем все баннеры пользователя
        const result = await apiService.getUserBanners(user.id, onboardingData?.campaign_id);
        console.log('✅ Step8: Banners loaded successfully', {
          bannersCount: result.banners.length,
          banners: result.banners
        });
        setBanners(result.banners);
      } catch (err: any) {
        console.error('❌ Step8: Failed to load banners:', err);
        setError(err.message || 'Не удалось загрузить баннеры');
      } finally {
        setLoading(false);
      }
    };

    loadBanners();
  }, [user?.id, onboardingData?.campaign_id]);

  return (
    <Box sx={{ px: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
        Черновики готовы
      </Typography>

      {/* Таб навигация */}
      <Box sx={{ display: 'flex', borderBottom: 1, borderColor: '#304D69', mb: 3 }}>
        <Box 
          sx={{ 
            p: 2, 
            borderBottom: activeTab === 0 ? 3 : 0,
            borderColor: FigmaColors.primaryButton,
            color: activeTab === 0 ? FigmaColors.primaryButton : '#1e1f26',
            fontWeight: activeTab === 0 ? 700 : 400,
            cursor: 'pointer',
          }}
          onClick={() => setActiveTab(0)}
        >
          🔍 Яндекс.Директ
        </Box>
        <Box 
          sx={{ 
            p: 2, 
            borderBottom: activeTab === 1 ? 3 : 0,
            borderColor: FigmaColors.primaryButton,
            color: activeTab === 1 ? FigmaColors.primaryButton : '#1e1f26',
            fontWeight: activeTab === 1 ? 700 : 400,
            cursor: 'pointer',
          }}
          onClick={() => setActiveTab(1)}
        >
          🖼️ РСЯ и VK Ads
        </Box>
      </Box>

      {/* Контент вкладок */}
      {activeTab === 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, textAlign: 'center', py: 4 }}>
            Яндекс.Директ объявления будут доступны в следующих версиях
                </Typography>
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ mb: 4 }}>
          {loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
                Загружаем баннеры...
                </Typography>
              </Box>
          )}

          {error && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: '#f44336' }}>
                {error}
              </Typography>
            </Box>
          )}

          {!loading && !error && banners.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
                Баннеры не найдены
              </Typography>
            </Box>
          )}

          {!loading && banners.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2 }}>
              {banners.map((banner) => (
                <Card elevation={0} key={banner.id} sx={{ 
                  bgcolor: FigmaColors.inputBg, 
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <Box sx={{ 
                    width: '100%', 
                    height: 200, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: '#f5f5f5'
                  }}>
                    {banner.image_url ? (
                      <img 
                        src={normalizeImageUrl(banner.image_url)} 
                        alt={banner.title}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%', 
                          objectFit: 'contain' 
                        }}
                        onError={(e) => {
                          console.error('Banner image load error:', e);
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
                        Изображение недоступно
                      </Typography>
                    )}
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ 
                      color: '#000000',
                      fontSize: '1rem',
                      fontWeight: 600,
                      mb: 1
                    }}>
                      {banner.title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: FigmaColors.inputPlaceholder,
                      fontSize: '0.8rem',
                      mb: 1
                    }}>
                      {banner.image_size} • {banner.concept_name}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: FigmaColors.inputPlaceholder,
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2
                    }}>
                      {banner.prompt}
                    </Typography>
                  </CardContent>
          </Card>
        ))}
      </Box>
          )}
        </Box>
      )}

        <Button
          variant="contained"
          fullWidth
          onClick={nextStep}
          sx={{
            bgcolor: FigmaColors.primaryButton,
            color: FigmaColors.darkBg,
            borderRadius: 3,
            height: 48,
            fontWeight: 700,
            '&:hover': { 
            background: FigmaColors.primaryButtonGradient,
            opacity: 0.9,
            boxShadow: 'none',
            color: '#ffffff !important'
          },
          }}
        >
          Сохранить и перейти дальше
        </Button>
    </Box>
  );
}



function Step10({ 
  nextStep, 
  organizationData, 
  setOrganizationData, 
  isSubmitting, 
  submitError,
  handleOrganizationSubmit 
}: StepProps) {
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: keyof OrganizationData, value: string) => {
    if (setOrganizationData) {
      setOrganizationData(prev => ({ ...prev, [field]: value }));
    }
    // Очищаем ошибку для этого поля
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleOrganizationTypeSelect = (type: 'individual' | 'legal' | 'entrepreneur') => {
    if (setOrganizationData) {
      setOrganizationData(prev => ({ ...prev, organization_type: type }));
    }
    // Очищаем ошибку
    if (formErrors.organization_type) {
      setFormErrors(prev => ({ ...prev, organization_type: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!organizationData?.organization_type) {
      errors.organization_type = 'Выберите тип организации';
    }
    
    // Название организации обязательно только для ЮЛ и ИП
    if (organizationData?.organization_type === 'legal' || organizationData?.organization_type === 'entrepreneur') {
      if (!organizationData?.organization_name?.trim()) {
        errors.organization_name = 'Название организации обязательно';
      }
    }
    
    if (!organizationData?.tin?.trim()) {
      errors.tin = 'ИНН обязателен';
    } else if (!/^\d{10,12}$/.test(organizationData.tin.trim())) {
      errors.tin = 'ИНН должен содержать 10 или 12 цифр';
    }
    
    // Для физических лиц требуем ФИО
    if (organizationData?.organization_type === 'individual') {
      if (!organizationData?.representative_last_name?.trim()) {
        errors.representative_last_name = 'Фамилия обязательна';
      }
      
      if (!organizationData?.representative_first_name?.trim()) {
        errors.representative_first_name = 'Имя обязательно';
      }
    }
    
    if (!organizationData?.organization_email?.trim()) {
      errors.organization_email = 'Email обязателен';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(organizationData.organization_email.trim())) {
      errors.organization_email = 'Введите корректный email';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !handleOrganizationSubmit || !organizationData) {
      return;
    }

    const success = await handleOrganizationSubmit(organizationData);
    if (success) {
      nextStep(); // Переходим к следующему шагу после успешного сохранения
    }
  };

  const organizationTypeMapping = {
    'Физическое лицо': 'individual' as const,
    'Юридическое лицо': 'legal' as const,
    'ИП': 'entrepreneur' as const,
  };

  return (
    <Box sx={{ px: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
        Заполните данные для регистрации
      </Typography>
      <Typography variant="body2" sx={{ color: '#000000', mb: 3 }}>
        Данные будут использованы при регистрации аккаунтов в рекламных интеграторах.
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {/* Выбор типа организации - в самом верху формы */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: FigmaColors.headingColor }}>
        Вид организации
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {Object.entries(organizationTypeMapping).map(([label, value]) => (
          <Chip
            key={label}
            label={label}
            clickable
            variant={organizationData?.organization_type === value ? "filled" : "outlined"}
            onClick={() => handleOrganizationTypeSelect(value)}
            sx={{
              borderColor: organizationData?.organization_type === value ? '#000000' : '#ececfc',
              border: organizationData?.organization_type === value ? '1px solid #000000' : '1px solid #ececfc',
              color: organizationData?.organization_type === value ? '#ffffff' : '#514b81',
              bgcolor: organizationData?.organization_type === value ? '#000000' : '#ffffff',
              '&:hover': {
                bgcolor: organizationData?.organization_type === value ? '#000000' : '#ffffff',
                opacity: 0.8,
              },
            }}
          />
        ))}
      </Box>
      {formErrors.organization_type && (
        <Typography variant="caption" sx={{ color: '#f44336', mb: 2, display: 'block' }}>
          {formErrors.organization_type}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {/* Email - показываем для всех типов организаций */}
        <Box
          sx={{
            bgcolor: FigmaColors.inputBg,
            borderRadius: '36px',
            p: 2,
            border: formErrors.organization_email ? '1px solid #f44336' : 'none',
          }}
        >
          <input
            type="email"
            placeholder="Введите электронную почту"
            value={organizationData?.organization_email || ''}
            onChange={(e) => handleInputChange('organization_email', e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: FigmaColors.inputPlaceholder,
              fontSize: '16px',
              fontFamily: 'inherit',
            }}
          />
        </Box>
        {formErrors.organization_email && (
          <Typography variant="caption" sx={{ color: '#f44336', mt: -1 }}>
            {formErrors.organization_email}
          </Typography>
        )}

        {/* Название организации - только для ЮЛ и ИП */}
        {(organizationData?.organization_type === 'legal' || organizationData?.organization_type === 'entrepreneur') && (
          <>
            <Box
              sx={{
                bgcolor: FigmaColors.inputBg,
                borderRadius: '36px',
                p: 2,
                border: formErrors.organization_name ? '1px solid #f44336' : 'none',
              }}
            >
              <input
                type="text"
                placeholder="Название организации"
                value={organizationData?.organization_name || ''}
                onChange={(e) => handleInputChange('organization_name', e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: FigmaColors.inputPlaceholder,
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </Box>
            {formErrors.organization_name && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: -1 }}>
                {formErrors.organization_name}
              </Typography>
            )}
          </>
        )}

        {/* Поля ФИО показываем только для физических лиц */}
        {organizationData?.organization_type === 'individual' && (
          <>
            <Box
              sx={{
                bgcolor: FigmaColors.inputBg,
                borderRadius: '36px',
                p: 2,
                border: formErrors.representative_last_name ? '1px solid #f44336' : 'none',
              }}
            >
              <input
                type="text"
                placeholder="Фамилия (как в паспорте)"
                value={organizationData?.representative_last_name || ''}
                onChange={(e) => handleInputChange('representative_last_name', e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: FigmaColors.inputPlaceholder,
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </Box>
            {formErrors.representative_last_name && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: -1 }}>
                {formErrors.representative_last_name}
              </Typography>
            )}

            <Box
              sx={{
                bgcolor: FigmaColors.inputBg,
                borderRadius: '36px',
                p: 2,
                border: formErrors.representative_first_name ? '1px solid #f44336' : 'none',
              }}
            >
              <input
                type="text"
                placeholder="Имя (как в паспорте)"
                value={organizationData?.representative_first_name || ''}
                onChange={(e) => handleInputChange('representative_first_name', e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: FigmaColors.inputPlaceholder,
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </Box>
            {formErrors.representative_first_name && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: -1 }}>
                {formErrors.representative_first_name}
              </Typography>
            )}

            <Box
              sx={{
                bgcolor: FigmaColors.inputBg,
                borderRadius: '36px',
                p: 2,
              }}
            >
              <input
                type="text"
                placeholder="Отчество (как в паспорте, необязательно)"
                value={organizationData?.representative_middle_name || ''}
                onChange={(e) => handleInputChange('representative_middle_name', e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: FigmaColors.inputPlaceholder,
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </Box>
          </>
        )}

        {/* ИНН */}
        <Box
          sx={{
            bgcolor: FigmaColors.inputBg,
            borderRadius: '36px',
            p: 2,
            border: formErrors.tin ? '1px solid #f44336' : 'none',
          }}
        >
          <input
            type="text"
            placeholder="Введите ИНН"
            value={organizationData?.tin || ''}
            onChange={(e) => handleInputChange('tin', e.target.value.replace(/\D/g, ''))}
            maxLength={12}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: FigmaColors.inputPlaceholder,
              fontSize: '16px',
              fontFamily: 'inherit',
            }}
          />
        </Box>
        {formErrors.tin && (
          <Typography variant="caption" sx={{ color: '#f44336', mt: -1 }}>
            {formErrors.tin}
          </Typography>
        )}
        
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#666', 
            fontSize: '12px', 
            mt: 1, 
            display: 'block',
            lineHeight: 1.4
          }}
        >
          нужен для маркировки рекламы и автоматической передачи данных о рекламных кампаниях в Единый реестр интернет-рекламы (ЕРИР), как того требует закон о рекламе
        </Typography>
      </Box>

      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        disabled={isSubmitting}
        sx={{
          background: FigmaColors.primaryButtonGradient,
          color: '#ffffff !important',
          borderRadius: 3,
          height: 48,
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': { 
            background: FigmaColors.primaryButtonGradient,
            opacity: 0.9,
            boxShadow: 'none',
            color: '#ffffff !important'
          },
          '&:disabled': { 
            bgcolor: FigmaColors.inputBg, 
            color: '#000000' 
          },
        }}
      >
        {isSubmitting ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} sx={{ color: FigmaColors.darkBg }} />
            Сохраняем...
          </Box>
        ) : (
          'Сохранить и перейти дальше'
        )}
      </Button>
    </Box>
  );
}



const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
  const { user, isReady, isTelegramEnvironment } = useTelegramWebApp();
  const { user: authUser, updateOnboardingStep } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({});
  const [campaignData, setCampaignData] = useState<CampaignFormData>({
    goal: null,
    budget: '',
    geo: null,
  });
  const [organizationData, setOrganizationData] = useState<OrganizationData>({
    organization_name: '',
    organization_type: 'individual', // По умолчанию выбираем "Физическое лицо"
    tin: '',
    representative_last_name: '',
    representative_first_name: '',
    representative_middle_name: '',
    organization_email: '',
    registration_country: '',
    registration_region: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const dataLoadedRef = useRef(false);

  // Восстановить прогресс онбординга и загрузить сохраненные данные при монтировании
  useEffect(() => {
    console.log('🔍 OnboardingFlow: проверяем прогресс', {
      authUser,
      onboarding_step: authUser?.onboarding_step,
      onboarding_completed: authUser?.onboarding_completed,
      shouldRestore: authUser?.onboarding_step && !authUser.onboarding_completed
    });
    
    if (authUser?.onboarding_step && !authUser.onboarding_completed) {
      console.log('🔄 Восстанавливаем прогресс онбординга с шага:', authUser.onboarding_step);
      setCurrentStep(authUser.onboarding_step);
    }
  }, [authUser]);

  // Загрузка сохраненных данных онбординга при возвращении пользователя
  useEffect(() => {
    const loadSavedOnboardingData = async () => {
      // Проверяем условия для загрузки данных
      if (!user?.id || dataLoadedRef.current || isLoadingData) {
        return;
      }

      // Загружаем данные только если пользователь возвращается на шаг >= 3 
      // (после анализа URL есть сохраненные данные в кампании)
      const targetStep = authUser?.onboarding_step || currentStep;
      if (targetStep < 3) {
        console.log('🔍 OnboardingFlow: шаг меньше 3, пропускаем загрузку данных');
        return;
      }

      console.log('🔄 OnboardingFlow: загружаем сохраненные данные онбординга', {
        userId: user.id,
        targetStep
      });

      dataLoadedRef.current = true;
      setIsLoadingData(true);

      try {
        const savedData = await apiService.getOnboardingData(user.id);
        
        console.log('✅ OnboardingFlow: данные загружены', savedData);

        if (savedData.has_data) {
          // Восстанавливаем onboardingData
          const restoredOnboardingData: OnboardingData = {
            url: savedData.url,
            analysis: savedData.analysis,
            campaign_id: savedData.campaign_id,
            creatives: savedData.creatives,
          };
          
          setOnboardingData(restoredOnboardingData);
          console.log('✅ OnboardingFlow: onboardingData восстановлены', restoredOnboardingData);

          // Восстанавливаем campaignData
          if (savedData.campaign_form_data) {
            setCampaignData({
              goal: savedData.campaign_form_data.goal,
              budget: savedData.campaign_form_data.budget || '',
              geo: savedData.campaign_form_data.geo,
            });
            console.log('✅ OnboardingFlow: campaignData восстановлены', savedData.campaign_form_data);
          }
        } else {
          console.log('ℹ️ OnboardingFlow: нет сохраненных данных');
          // Если пользователь на шаге 3+ но данных нет - возвращаем на шаг 2 (ввод URL)
          if (targetStep >= 3) {
            console.log('⚠️ OnboardingFlow: на шаге 3+ но данных нет, возвращаем на шаг 2');
            setCurrentStep(2);
          }
        }
      } catch (error) {
        console.error('❌ OnboardingFlow: ошибка загрузки данных', error);
        // При ошибке загрузки данных на шаге 3+ - возвращаем на шаг 2
        if (targetStep >= 3) {
          console.log('⚠️ OnboardingFlow: ошибка загрузки данных на шаге 3+, возвращаем на шаг 2');
          setCurrentStep(2);
        }
      } finally {
        setIsLoadingData(false);
      }
    };

    loadSavedOnboardingData();
  }, [user?.id, authUser?.onboarding_step, currentStep]);

  // Обработчик для отправки кампании из Step4
  const handleCampaignSubmit = async (campaignPayload: any) => {
    setIsSubmitting(true);
    setSubmitError('');
    
    // Подробное логирование для отладки
    console.log('🔍 Debug Telegram WebApp state:', {
      isReady,
      isTelegramEnvironment,
      user,
      userId: user?.id,
      userIdType: typeof user?.id
    });
    
    // Проверяем готовность WebApp
    if (!isReady) {
      console.error('❌ Telegram WebApp not ready yet!');
      setSubmitError('Telegram WebApp еще не готов. Попробуйте еще раз.');
      setIsSubmitting(false);
      return false;
    }
    
    // Проверяем наличие пользователя Telegram
    if (!user?.id) {
      console.error('❌ No user.id found!', { user, isTelegramEnvironment });
      setSubmitError('Необходимо авторизоваться через Telegram для создания кампании');
      setIsSubmitting(false);
      return false;
    }
    
    try {
      // Добавляем telegram_user_id к данным кампании
      const campaignWithUserId = {
        ...campaignPayload,
        telegram_user_id: user.id
      };
      
      // Логируем данные для отладки
      console.log('🚀 Отправляем кампанию:', campaignWithUserId);
      console.log('📊 Telegram user:', user);
      
      const result = await apiService.createCampaignFromOnboarding(campaignWithUserId);
      
      // ВАЖНО: Сохраняем campaign_id в onboardingData для использования в Step 6
      console.log('✅ Step4: Campaign created with ID:', result.campaign_id);
      setOnboardingData(prev => ({
        ...prev,
        campaign_id: result.campaign_id
      }));
      
      return true; // Успешно
    } catch (error: any) {
      const errorMessage = error?.message || 'Не удалось создать кампанию';
      setSubmitError(errorMessage);
      return false; // Ошибка
    } finally {
      setIsSubmitting(false);
    }
  };


  // Обработчик для отправки данных организации из Step10
  const handleOrganizationSubmit = async (organizationPayload: OrganizationData) => {
    setIsSubmitting(true);
    setSubmitError('');
    
    // Проверяем наличие пользователя Telegram
    if (!user?.id) {
      setSubmitError('Необходимо авторизоваться через Telegram для сохранения данных организации');
      setIsSubmitting(false);
      return false;
    }
    
    try {
      // Сначала сохраняем данные организации
      const apiPayload = {
        telegram_user_id: user.id,
        organization_name: organizationPayload.organization_name.trim(),
        organization_type: organizationPayload.organization_type!,
        tin: organizationPayload.tin.trim(),
        representative_last_name: organizationPayload.representative_last_name.trim(),
        representative_first_name: organizationPayload.representative_first_name.trim(),
        representative_middle_name: organizationPayload.representative_middle_name?.trim() || undefined,
        organization_email: organizationPayload.organization_email.trim(),
        registration_country: organizationPayload.registration_country?.trim() || undefined,
        registration_region: organizationPayload.registration_region?.trim() || undefined,
        currency: 'RUB',
        notification_language: 'RU',
      };
      
      console.log('🏢 Отправляем данные организации:', apiPayload);
      
      const result = await apiService.saveOrganizationData(apiPayload);
      
      console.log('✅ Данные организации сохранены:', result);
      
      // Показываем информацию о результате создания в Яндекс.Директ
      if (result.yandex_direct_error) {
        console.warn('⚠️ Ошибка создания организации в Яндекс.Директ:', result.yandex_direct_error);
      } else if (result.yandex_direct_created) {
        console.log('🎉 Организация успешно создана в Яндекс.Директ');
      }

      // Теперь запускаем завершение онбординга с созданием в VK Ads
      if (onboardingData.campaign_id) {
        console.log('🚀 Запускаем завершение онбординга с созданием в VK Ads');
        
        try {
          const completionResult = await apiService.completeOnboarding({
            telegram_user_id: user.id,
            campaign_id: onboardingData.campaign_id,
            organization_data: {
              organization_name: organizationPayload.organization_name.trim(),
              organization_type: organizationPayload.organization_type!,
              tin: organizationPayload.tin.trim(),
              representative_last_name: organizationPayload.representative_last_name.trim(),
              representative_first_name: organizationPayload.representative_first_name.trim(),
              representative_middle_name: organizationPayload.representative_middle_name?.trim(),
              organization_email: organizationPayload.organization_email.trim(),
              registration_country: organizationPayload.registration_country?.trim(),
              registration_region: organizationPayload.registration_region?.trim(),
            }
          });
          
          console.log('✅ Онбординг завершен, процесс создания рекламы запущен:', completionResult);
          
          // Показываем результаты создания в VK Ads
          if (completionResult.data.vk_ads.errors.length > 0) {
            console.warn('⚠️ Есть ошибки при создании в VK Ads:', completionResult.data.vk_ads.errors);
          } else {
            console.log('🎉 VK Ads реклама успешно создана');
          }

          // Показываем результаты создания в Яндекс.Директ
          if (completionResult.data.yandex_direct.errors.length > 0) {
            console.warn('⚠️ Есть ошибки при создании в Яндекс.Директ:', completionResult.data.yandex_direct.errors);
          } else if (completionResult.data.yandex_direct.existing_client_used) {
            console.log('🎉 Кампания в Яндекс.Директ создана для существующего клиента');
          } else {
            console.log('ℹ️ Создание кампании в Яндекс.Директ пропущено (нет данных клиента)');
          }

          // Показываем глобальные ошибки
          if (completionResult.data.global_errors.length > 0) {
            console.error('❌ Глобальные ошибки:', completionResult.data.global_errors);
          }
          
        } catch (vkError: any) {
          console.error('❌ Ошибка при завершении онбординга:', vkError);
          // Не блокируем завершение онбординга из-за ошибок VK Ads
          // Пользователь все равно может перейти на главную страницу
        }
      }
      
      return true; // Успешно
    } catch (error: any) {
      const errorMessage = error?.message || 'Не удалось сохранить данные организации';
      setSubmitError(errorMessage);
      return false; // Ошибка
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Обновляем прогресс только на шагах, где он виден (исключая Step3 и Step6, которые управляются отдельно)
    const timerProgressSteps = new Set([11]);
    if (!timerProgressSteps.has(currentStep)) {
      return;
    }

    setAnimatedProgress(0);
    const duration = 10000; // 10 секунд
    const intervalTime = 50;
    const increment = 100 / (duration / intervalTime);

    const timer = setInterval(() => {
      setAnimatedProgress((prev) => {
        const nextValue = prev + increment;
        if (nextValue >= 100) {
          clearInterval(timer);
          return 100;
        }
        return nextValue;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [currentStep]);

  const nextStep = async () => {
    if (currentStep < 9) { // Теперь максимум 9 шагов (Step1-Step4, Step6-Step8, Step10)
      // Пропускаем Step5 (выбор способа пополнения бюджета) и Step9
      let nextStepNumber;
      if (currentStep === 4) {
        nextStepNumber = 6; // Step4 -> Step6
      } else if (currentStep === 7) {
        nextStepNumber = 9; // Step7 -> Step10 (case 9) - ввод реквизитов
      } else if (currentStep === 8) {
        nextStepNumber = 9; // Step8 -> Step10 (case 9)
      } else {
        nextStepNumber = currentStep + 1;
      }
      
      // Сохраняем прогресс в БД (не блокируем переход при ошибке)
      try {
        await updateOnboardingStep(nextStepNumber);
      } catch (error) {
        console.error('Failed to save onboarding progress, but continuing:', error);
      }
      
      setCurrentStep(nextStepNumber);
    } else {
      onComplete(); // После Step10 (case 9) завершаем онбординг
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      // Обратная логика пропуска шагов
      let prevStepNumber;
      if (currentStep === 6) {
        prevStepNumber = 4; // Step6 -> Step4
      } else if (currentStep === 9) {
        prevStepNumber = 7; // Step10 -> Step7
      } else {
        prevStepNumber = currentStep - 1;
      }
      setCurrentStep(prevStepNumber);
    }
  };

  const StepComponent = useMemo(() => {
    switch (currentStep) {
      case 1: return Step1;
      case 2: return Step2;
      case 3: return Step3;
      case 4: return Step4;
      case 6: return Step6;
      case 7: return Step7;
      case 8: return Step8;
      case 9: return Step10; // Step8 -> Step10 (пропускаем удаленный Step5 и Step9)
      default: return Step1;
    }
  }, [currentStep]);

  // Показываем загрузку пока восстанавливаем данные
  if (isLoadingData && currentStep >= 4) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#ffffff', 
        color: '#000000',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" sx={{ color: '#666' }}>
          Загружаем ваши данные...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#ffffff', 
      color: '#000000',
      overflow: 'auto',
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        p: 2, 
        pt: 3 
      }}>
        {currentStep > 1 && (
          <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center', color: '#000000' }}>
            Быстрый запуск рекламы
          </Typography>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ py: 2, pb: 4 }}>
        <StepComponent
          nextStep={nextStep}
          prevStep={prevStep}
          animatedProgress={animatedProgress}
          onComplete={onComplete}
          onboardingData={onboardingData}
          setOnboardingData={setOnboardingData}
          user={user}
          isAnalyzing={false}
          analysisError=""
          campaignData={campaignData}
          setCampaignData={setCampaignData}
          organizationData={organizationData}
          setOrganizationData={setOrganizationData}
          isSubmitting={isSubmitting}
          submitError={submitError}
          handleCampaignSubmit={handleCampaignSubmit}
          handleOrganizationSubmit={handleOrganizationSubmit}
        />
      </Box>
    </Box>
  );
};

export default OnboardingFlow;
